/**
 * Texte d'une ligne pour le moteur v2 (`track = 'packs'`, lot V3).
 *
 * L'app construit une ligne avec des bouts ; l'ancien moteur lit les bouts, le moteur v2
 * lit du TEXTE (celui qui est enregistré, `line_text`, et que le serveur rejoue). Le texte
 * affiché (`lineText`) suffit presque toujours, sauf pour une fonction dont l'argument
 * n'a pas de parenthèses : l'ancien moteur lit « exp x » (le bout « e^x »), le moteur v2
 * veut « exp(x) ».
 *
 * L'exponentielle : un seul bout dans l'app, deux écritures pour le moteur v2 (« exp(x) »
 * et « e^x », passer de l'une à l'autre est un geste, « Notation eˣ »). La ligne suit donc
 * l'écriture du corrigé de la feuille (`expSpelling`). Pur.
 */
import { isFunc, type ScratchModel } from '@jeraisonne/reasoner';
import { atomItem, isFrac, lineText, type LineItem } from './line-items';

export type ExpSpelling = 'exp' | 'e^';

/** L'écriture de l'exponentielle dans le corrigé : celle de la réponse, sinon la plus fréquente. */
export function expSpelling(model: ScratchModel | null | undefined): ExpSpelling {
  const k = model?.kernel;
  const count = (texts: (string | undefined)[]) => {
    const t = texts.filter(Boolean).join(' ');
    return { exp: (t.match(/exp\s*\(/g) ?? []).length, pow: (t.match(/e\s*\^/g) ?? []).length };
  };
  const answer = count([k?.answer]);
  if (answer.exp !== answer.pow) return answer.exp > answer.pow ? 'exp' : 'e^';
  const all = count([k?.start, k?.answer, ...(k?.path ?? []), ...(model?.givens ?? [])]);
  return all.exp > all.pow ? 'exp' : 'e^';
}

/** Une fonction suivie d'un bout sans parenthèse : l'argument est mis entre parenthèses. */
export function withFunctionParens(items: LineItem[], spelling: ExpSpelling = 'e^'): LineItem[] {
  const out: LineItem[] = [];
  for (let i = 0; i < items.length; i++) {
    const c = items[i]!;
    const fn = !isFrac(c) && c.atoms.length === 1 && isFunc(c.atoms[0]) ? c.atoms[0] : null;
    out.push(fn === 'exp' && spelling === 'e^' ? atomItem(['exp'], 'e^') : c);
    const next = items[i + 1];
    if (!fn || !next) continue;
    if (!isFrac(next) && next.atoms[0] === '(') continue;
    out.push(atomItem(['('], '('), next, atomItem([')'], ')'));
    i++;
  }
  return out;
}

export function packsLineText(items: LineItem[], spelling: ExpSpelling = 'e^'): string {
  // un tableau (de signes « ⊞{…} », à remplir « ⊟{…} ») est un seul bout : son texte est le bout
  const only = items.length === 1 && !isFrac(items[0]!) ? items[0]!.atoms : null;
  if (only?.length === 1 && /^[⊞⊟]\{/.test(only[0]!)) return only[0]!;
  return lineText(withFunctionParens(items, spelling));
}
